import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { normalizeLanguageCode, isSupportedLanguage } from '@/lib/languageUtils';

// Only English is statically imported — always available without async loading.
// All other languages are lazy-loaded on demand via dynamic import().
import enTranslation from '../locales/en/translation.json';
import enCommon from '../locales/en/common.json';
import enDashboard from '../locales/en/dashboard.json';
import enCCTVDesigner from '../locales/en/cctv-designer.json';
import enValidation from '../locales/en/validation.json';
import enCalculators from '../locales/en/calculators.json';
import enRodo from '../locales/en/rodo.json';
import enLens from '../locales/en/lens.json';

// Lazy loaders for all non-English languages (code-split per language)
const languageLoaders: Record<string, Record<string, () => Promise<any>>> = {
  es: {
    translation: () => import('../locales/es/translation.json'),
    common: () => import('../locales/es/common.json'),
    dashboard: () => import('../locales/es/dashboard.json'),
    'cctv-designer': () => import('../locales/es/cctv-designer.json'),
    validation: () => import('../locales/es/validation.json'),
    calculators: () => import('../locales/es/calculators.json'),
    rodo: () => import('../locales/es/rodo.json'),
    lens: () => import('../locales/es/lens.json'),
  },
  fr: {
    translation: () => import('../locales/fr/translation.json'),
    common: () => import('../locales/fr/common.json'),
    dashboard: () => import('../locales/fr/dashboard.json'),
    'cctv-designer': () => import('../locales/fr/cctv-designer.json'),
    validation: () => import('../locales/fr/validation.json'),
    calculators: () => import('../locales/fr/calculators.json'),
    rodo: () => import('../locales/fr/rodo.json'),
    lens: () => import('../locales/fr/lens.json'),
  },
  ar: {
    translation: () => import('../locales/ar/translation.json'),
    common: () => import('../locales/ar/common.json'),
    dashboard: () => import('../locales/ar/dashboard.json'),
    'cctv-designer': () => import('../locales/ar/cctv-designer.json'),
    validation: () => import('../locales/ar/validation.json'),
    calculators: () => import('../locales/ar/calculators.json'),
    rodo: () => import('../locales/ar/rodo.json'),
    lens: () => import('../locales/ar/lens.json'),
  },
  'pt-pt': {
    translation: () => import('../locales/pt-pt/translation.json'),
    common: () => import('../locales/pt-pt/common.json'),
    dashboard: () => import('../locales/pt-pt/dashboard.json'),
    'cctv-designer': () => import('../locales/pt-pt/cctv-designer.json'),
    validation: () => import('../locales/pt-pt/validation.json'),
    calculators: () => import('../locales/pt-pt/calculators.json'),
    rodo: () => import('../locales/pt-pt/rodo.json'),
    lens: () => import('../locales/pt-pt/lens.json'),
  },
  af: {
    translation: () => import('../locales/af/translation.json'),
    common: () => import('../locales/af/common.json'),
    dashboard: () => import('../locales/af/dashboard.json'),
    'cctv-designer': () => import('../locales/af/cctv-designer.json'),
    validation: () => import('../locales/af/validation.json'),
    calculators: () => import('../locales/af/calculators.json'),
    rodo: () => import('../locales/af/rodo.json'),
    lens: () => import('../locales/af/lens.json'),
  },
  zu: {
    translation: () => import('../locales/zu/translation.json'),
    common: () => import('../locales/zu/common.json'),
    dashboard: () => import('../locales/zu/dashboard.json'),
    'cctv-designer': () => import('../locales/zu/cctv-designer.json'),
    validation: () => import('../locales/zu/validation.json'),
    calculators: () => import('../locales/zu/calculators.json'),
    rodo: () => import('../locales/zu/rodo.json'),
    lens: () => import('../locales/zu/lens.json'),
  },
};

const enResources = {
  en: {
    translation: enTranslation,
    common: enCommon,
    dashboard: enDashboard,
    'cctv-designer': enCCTVDesigner,
    validation: enValidation,
    calculators: enCalculators,
    rodo: enRodo,
    lens: enLens,
  },
};

// Detect language from the current URL path (for hydration matching)
function getUrlLanguage(): string {
  if (typeof window === 'undefined') return 'en';
  const segments = window.location.pathname.split('/').filter(Boolean);
  const LANG_PREFIXES = ['es', 'fr', 'ar', 'pt-pt', 'af', 'zu'];
  return (segments[0] && LANG_PREFIXES.includes(segments[0])) ? segments[0] : 'en';
}

// Get saved language from localStorage or default to English
const getSavedLanguage = (): string => {
  if (import.meta.env.SSR) return 'en';
  try {
    const saved = localStorage.getItem('i18n_language');
    if (saved) {
      const normalized = normalizeLanguageCode(saved);
      if (normalized && isSupportedLanguage(normalized)) {
        return normalized;
      }
    }
  } catch (e) {
    console.error('Error reading language preference:', e);
  }
  return 'en';
};

// Initialize i18next with English only. Other languages are loaded on demand.
// During SSG and client hydration, the correct language is loaded before rendering
// via the async callback in main.tsx.
const initLang = import.meta.env.SSR ? 'en' : getUrlLanguage();

i18n
  .use(initReactI18next)
  .init({
    resources: enResources,
    fallbackLng: 'en',
    defaultNS: 'translation',
    ns: ['translation', 'common', 'dashboard', 'cctv-designer', 'validation', 'calculators', 'rodo', 'lens'],
    lng: initLang,
    interpolation: {
      escapeValue: false,
    },
    lowerCaseLng: true,
    supportedLngs: ['en', 'es', 'fr', 'ar', 'pt-pt', 'af', 'zu'],
    fallbackNS: 'translation',
  });

// Load a language on demand — fetches all missing namespaces via dynamic import
export const loadLanguage = async (lang: string): Promise<void> => {
  if (lang === 'en') return; // English is always available

  const loaders = languageLoaders[lang];
  if (!loaders) return;

  // Find namespaces that aren't loaded yet
  const missing = Object.entries(loaders).filter(
    ([ns]) => !i18n.hasResourceBundle(lang, ns)
  );

  if (missing.length === 0) return;

  try {
    const results = await Promise.all(
      missing.map(async ([ns, loader]) => {
        const module = await loader();
        return [ns, module.default || module] as const;
      })
    );
    for (const [ns, data] of results) {
      i18n.addResourceBundle(lang, ns, data, true, true);
    }
  } catch (error) {
    console.error(`Error loading language ${lang}:`, error);
  }
};

// Call this AFTER hydration (e.g. in a useEffect) to restore the user's saved language
export const restoreSavedLanguage = async (): Promise<void> => {
  const savedLang = getSavedLanguage();
  if (savedLang !== 'en') {
    await loadLanguage(savedLang);
    await i18n.changeLanguage(savedLang);
  }
};

/**
 * Extract language from a URL path and set i18n to that language SYNCHRONOUSLY.
 * Used during SSG and client hydration after resources have been loaded.
 *
 * IMPORTANT: call loadLanguage(lang) first to ensure resources are available.
 */
export const setLanguageFromPath = (pathname: string): string => {
  const LANG_PREFIXES = ['es', 'fr', 'ar', 'pt-pt', 'af', 'zu'];
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];
  const lang = (firstSegment && LANG_PREFIXES.includes(firstSegment)) ? firstSegment : 'en';

  // Synchronous language switch — sets internal state directly.
  i18n.language = lang;
  // @ts-expect-error i18next internal API
  i18n.languages = [lang, 'en'];
  // @ts-expect-error i18next internal API
  i18n.resolvedLanguage = lang;

  i18n.emit('languageChanged', lang);

  return lang;
};

export default i18n;
